// UltraFlex - Member Agreement Flow (Digital version of paper form)
// Used in: 1) Signup wizard (after personal details) 2) Migration gate (existing members)
import React, { useState, useEffect, useRef } from 'react';
import {
  Shield, Heart, Snowflake, FileText, PenTool,
  ChevronRight, ChevronLeft, Check, AlertTriangle, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collect device forensics from the browser
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let browserName = 'Unknown', browserVersion = '', osName = 'Unknown', osVersion = '', deviceType = 'desktop';
  // Browser detection
  if (ua.includes('Firefox/')) { browserName = 'Firefox'; browserVersion = ua.split('Firefox/')[1]?.split(' ')[0]; }
  else if (ua.includes('Edg/')) { browserName = 'Edge'; browserVersion = ua.split('Edg/')[1]?.split(' ')[0]; }
  else if (ua.includes('Chrome/')) { browserName = 'Chrome'; browserVersion = ua.split('Chrome/')[1]?.split(' ')[0]; }
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) { browserName = 'Safari'; browserVersion = ua.split('Version/')[1]?.split(' ')[0]; }
  // OS detection
  if (ua.includes('Windows')) { osName = 'Windows'; osVersion = ua.match(/Windows NT ([\d.]+)/)?.[1] || ''; }
  else if (ua.includes('Mac OS X')) { osName = 'macOS'; osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''; }
  else if (ua.includes('Android')) { osName = 'Android'; osVersion = ua.match(/Android ([\d.]+)/)?.[1] || ''; }
  else if (ua.includes('iPhone') || ua.includes('iPad')) { osName = 'iOS'; osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''; }
  else if (ua.includes('Linux')) { osName = 'Linux'; }
  // Device type
  if (/Mobi|Android.*Mobile|iPhone/.test(ua)) deviceType = 'mobile';
  else if (/iPad|Android(?!.*Mobile)|Tablet/.test(ua)) deviceType = 'tablet';

  return {
    user_agent: ua,
    browser_name: browserName,
    browser_version: browserVersion || '',
    os_name: osName,
    os_version: osVersion || '',
    device_type: deviceType,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    referrer: document.referrer || '',
    platform: navigator.platform || '',
    cookie_id: document.cookie.match(/uf_tracking=([^;]+)/)?.[1] || '',
  };
};

const STEPS = [
  { id: 'declaration', label: 'Declaration', icon: Shield },
  { id: 'health_safety', label: 'Health & Safety', icon: Heart },
  { id: 'parq', label: 'PAR-Q Health', icon: AlertTriangle },
  { id: 'waiver', label: 'Ice Bath & Sauna', icon: Snowflake },
  { id: 'terms', label: 'Terms & Conditions', icon: FileText },
  { id: 'signature', label: 'Digital Signature', icon: PenTool },
];

const MemberAgreementFlow = ({ memberId, memberName, onComplete, isMigration = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [startedAt] = useState(new Date().toISOString());
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  // Form state
  const [decl, setDecl] = useState({
    accurate_health_info: false, timely_payment: false,
    gym_rights: false, follow_rules: false, not_responsible_belongings: false,
  });
  const [hs, setHs] = useState({
    attend_induction: false, proper_attire: false, no_substances: false,
    use_as_instructed: false, cctv_acknowledged: false, report_hazards: false,
  });
  const [parq, setParq] = useState({
    supervised_activity: false, chest_pain: false, dizziness: false,
    bone_joint_issues: false, pregnant_postpartum: false, recent_surgery: false,
    chronic_illness: false, chronic_details: '', exercise_impact: '',
    doctor_name: '', doctor_phone: '',
  });
  const [waiver, setWaiver] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [signature, setSignature] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const allDeclChecked = Object.values(decl).every(Boolean);
  const allHsChecked = Object.values(hs).every(Boolean);

  const canProceed = () => {
    switch (currentStep) {
      case 0: return allDeclChecked;
      case 1: return allHsChecked;
      case 2: return parq.doctor_name.trim().length > 0;
      case 3: return waiver;
      case 4: return termsAccepted;
      case 5: return signature.trim().length >= 3;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setSubmitting(true);
    const deviceInfo = getDeviceInfo();

    const payload = {
      member_id: memberId,
      agreement_version: '2025-01',
      is_migration: isMigration,
      started_at: startedAt,
      // Declaration
      decl_accurate_health_info: decl.accurate_health_info,
      decl_timely_payment: decl.timely_payment,
      decl_gym_rights: decl.gym_rights,
      decl_follow_rules: decl.follow_rules,
      decl_not_responsible_belongings: decl.not_responsible_belongings,
      // Health & Safety
      hs_attend_induction: hs.attend_induction,
      hs_proper_attire: hs.proper_attire,
      hs_no_substances: hs.no_substances,
      hs_use_as_instructed: hs.use_as_instructed,
      hs_cctv_acknowledged: hs.cctv_acknowledged,
      hs_report_hazards: hs.report_hazards,
      // PAR-Q
      parq_supervised_activity: parq.supervised_activity,
      parq_chest_pain: parq.chest_pain,
      parq_dizziness: parq.dizziness,
      parq_bone_joint_issues: parq.bone_joint_issues,
      parq_pregnant_postpartum: parq.pregnant_postpartum,
      parq_recent_surgery: parq.recent_surgery,
      parq_chronic_illness: parq.chronic_illness,
      parq_chronic_details: parq.chronic_details,
      parq_exercise_impact: parq.exercise_impact,
      parq_doctor_name: parq.doctor_name,
      parq_doctor_phone: parq.doctor_phone,
      // Waiver
      waiver_ice_sauna_accepted: waiver,
      // Terms
      terms_accepted: termsAccepted,
      terms_version: '2024-10-01',
      marketing_opt_in: marketingOptIn,
      // Signature
      signature_full_name: signature,
      signature_date: new Date().toISOString().split('T')[0],
      // Device
      device_info: deviceInfo,
    };

    try {
      const res = await fetch(`${API_URL}/api/agreements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        onComplete?.();
      } else {
        alert('Failed to submit agreement. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  const next = () => {
    if (currentStep === 5) { handleSubmit(); return; }
    if (canProceed()) setCurrentStep(prev => prev + 1);
  };
  const back = () => setCurrentStep(prev => Math.max(0, prev - 1));

  const Checkbox = ({ checked, onChange, label, testId }) => (
    <label className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg cursor-pointer hover:bg-zinc-900 transition-colors group" data-testid={testId}>
      <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-uf-red border-uf-red' : 'border-zinc-600 group-hover:border-zinc-400'}`}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <span className="text-sm text-zinc-300 leading-relaxed">{label}</span>
    </label>
  );

  const YesNo = ({ label, value, onChange, testId }) => (
    <div className="flex items-start justify-between gap-4 p-3 bg-zinc-900/50 rounded-lg" data-testid={testId}>
      <span className="text-sm text-zinc-300 flex-1">{label}</span>
      <div className="flex gap-2 flex-shrink-0">
        <button type="button" onClick={() => onChange(true)}
          className={`px-3 py-1 rounded text-xs font-medium ${value ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40' : 'bg-zinc-800 text-zinc-500'}`}>YES</button>
        <button type="button" onClick={() => onChange(false)}
          className={`px-3 py-1 rounded text-xs font-medium ${!value ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40' : 'bg-zinc-800 text-zinc-500'}`}>NO</button>
      </div>
    </div>
  );

  const handleTermsScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) setTermsScrolled(true);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="agreement-flow">
      {/* Progress */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs whitespace-nowrap ${i === currentStep ? 'bg-uf-red/20 text-uf-red font-semibold' : i < currentStep ? 'text-emerald-400' : 'text-zinc-600'}`}>
              {i < currentStep ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-4 h-px flex-shrink-0 ${i < currentStep ? 'bg-emerald-500' : 'bg-zinc-800'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div ref={scrollRef} className="bg-uf-surface border border-uf-border rounded-xl overflow-hidden">
        {/* Step Header */}
        <div className="bg-zinc-900/50 px-6 py-4 border-b border-uf-border">
          <div className="flex items-center gap-3">
            {React.createElement(STEPS[currentStep].icon, { className: 'w-5 h-5 text-uf-red' })}
            <h2 className="text-lg font-bold text-white">{STEPS[currentStep].label}</h2>
          </div>
          {isMigration && currentStep === 0 && (
            <div className="mt-2 flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-2 rounded-lg">
              <Info className="w-4 h-4 flex-shrink-0" />
              As an existing member, please review and confirm these updated terms to continue using UltraFlex services.
            </div>
          )}
        </div>

        <div className="p-6 space-y-3">
          {/* === STEP 0: DECLARATION === */}
          {currentStep === 0 && (
            <>
              <p className="text-zinc-400 text-sm mb-4">Please read and confirm each declaration:</p>
              <Checkbox testId="decl-health" checked={decl.accurate_health_info} onChange={() => setDecl(p => ({...p, accurate_health_info: !p.accurate_health_info}))}
                label="My membership includes access to the gym facilities and an induction session. I have provided accurate health information through the PAR-Q form as required." />
              <Checkbox testId="decl-payment" checked={decl.timely_payment} onChange={() => setDecl(p => ({...p, timely_payment: !p.timely_payment}))}
                label="I will ensure timely payment of my membership fees and acknowledge that failure to pay may result in a £75 administration fee and referral to a debt collection agency (this is not applicable to day/week passes)." />
              <Checkbox testId="decl-rights" checked={decl.gym_rights} onChange={() => setDecl(p => ({...p, gym_rights: !p.gym_rights}))}
                label="The gym reserves the right to deny entry, terminate memberships, and alter facility hours, pricing, and equipment availability without notice. Memberships are strictly non-transferable, non-negotiable, and non-refundable." />
              <Checkbox testId="decl-rules" checked={decl.follow_rules} onChange={() => setDecl(p => ({...p, follow_rules: !p.follow_rules}))}
                label="I agree to follow all gym rules, including: replacing weights and unloading machines after use; avoiding any damage to gym equipment or property and reporting any hazards immediately; using only the gym's designated machine pins and refraining from bringing personal pins or removing gym pins." />
              <Checkbox testId="decl-belongings" checked={decl.not_responsible_belongings} onChange={() => setDecl(p => ({...p, not_responsible_belongings: !p.not_responsible_belongings}))}
                label="I understand that the gym is not responsible for personal injuries, loss, or damage to my belongings." />
            </>
          )}

          {/* === STEP 1: HEALTH & SAFETY === */}
          {currentStep === 1 && (
            <>
              <p className="text-zinc-400 text-sm mb-4">As a member of UltraFlex Gym, I agree to:</p>
              <Checkbox testId="hs-induction" checked={hs.attend_induction} onChange={() => setHs(p => ({...p, attend_induction: !p.attend_induction}))}
                label="Attend a gym induction, or if declined, assume responsibility for safe and appropriate equipment use." />
              <Checkbox testId="hs-attire" checked={hs.proper_attire} onChange={() => setHs(p => ({...p, proper_attire: !p.proper_attire}))}
                label="Wear proper workout attire and footwear at all times." />
              <Checkbox testId="hs-substances" checked={hs.no_substances} onChange={() => setHs(p => ({...p, no_substances: !p.no_substances}))}
                label="Refrain from gym use under the influence of alcohol or any substances." />
              <Checkbox testId="hs-equipment" checked={hs.use_as_instructed} onChange={() => setHs(p => ({...p, use_as_instructed: !p.use_as_instructed}))}
                label="Use equipment as instructed by staff, seeking assistance if needed." />
              <Checkbox testId="hs-cctv" checked={hs.cctv_acknowledged} onChange={() => setHs(p => ({...p, cctv_acknowledged: !p.cctv_acknowledged}))}
                label="Acknowledge that CCTV is in place to enhance safety and monitor accidents." />
              <Checkbox testId="hs-hazards" checked={hs.report_hazards} onChange={() => setHs(p => ({...p, report_hazards: !p.report_hazards}))}
                label="Report any spills or hazards to staff immediately to prevent injury or accident." />
            </>
          )}

          {/* === STEP 2: PAR-Q === */}
          {currentStep === 2 && (
            <>
              <p className="text-zinc-400 text-sm mb-1">All members must complete this form. Access to the gym will be denied without completion.</p>
              <p className="text-amber-400 text-xs mb-4">If you answer YES to any question, please consult your doctor before beginning exercise.</p>
              <div className="space-y-2">
                <YesNo testId="parq-supervised" label="Have you been advised to engage only in medically supervised physical activity?" value={parq.supervised_activity} onChange={v => setParq(p => ({...p, supervised_activity: v}))} />
                <YesNo testId="parq-chest" label="Do you experience chest pain during or after physical activity?" value={parq.chest_pain} onChange={v => setParq(p => ({...p, chest_pain: v}))} />
                <YesNo testId="parq-dizziness" label="Have you experienced loss of balance, dizziness, or loss of consciousness?" value={parq.dizziness} onChange={v => setParq(p => ({...p, dizziness: v}))} />
                <YesNo testId="parq-bone" label="Do you have any bone, joint, or other medical issues that could impact your exercise routine (e.g., diabetes, high blood pressure)?" value={parq.bone_joint_issues} onChange={v => setParq(p => ({...p, bone_joint_issues: v}))} />
                <YesNo testId="parq-pregnant" label="Are you currently pregnant or have you given birth within the past six months?" value={parq.pregnant_postpartum} onChange={v => setParq(p => ({...p, pregnant_postpartum: v}))} />
                <YesNo testId="parq-surgery" label="Have you had recent surgery?" value={parq.recent_surgery} onChange={v => setParq(p => ({...p, recent_surgery: v}))} />
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <YesNo testId="parq-chronic" label="Do you have any chronic illnesses, injuries, or medications?" value={parq.chronic_illness} onChange={v => setParq(p => ({...p, chronic_illness: v, ...(!v ? { chronic_details: '', exercise_impact: '' } : {})}))} />
                {parq.chronic_illness && (
                  <div className="mt-3 space-y-3 pl-4 border-l-2 border-amber-500/30">
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">Please specify your condition(s):</label>
                      <textarea value={parq.chronic_details} onChange={e => setParq(p => ({...p, chronic_details: e.target.value}))}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" rows={2} data-testid="parq-chronic-details" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">How does this impact your ability to exercise?</label>
                      <textarea value={parq.exercise_impact} onChange={e => setParq(p => ({...p, exercise_impact: e.target.value}))}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" rows={2} data-testid="parq-exercise-impact" />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <p className="text-zinc-400 text-sm mb-3">Your Doctor's Details (required for health records):</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Doctor's Name *</label>
                    <input value={parq.doctor_name} onChange={e => setParq(p => ({...p, doctor_name: e.target.value}))}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="parq-doctor-name" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Doctor's Phone</label>
                    <input value={parq.doctor_phone} onChange={e => setParq(p => ({...p, doctor_phone: e.target.value}))}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="parq-doctor-phone" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* === STEP 3: ICE BATH & SAUNA WAIVER === */}
          {currentStep === 3 && (
            <>
              <div className="bg-zinc-900/50 rounded-lg p-4 mb-4 text-sm text-zinc-300 leading-relaxed space-y-3">
                <p className="font-semibold text-white">Ice Bath & Infrared Sauna Waiver</p>
                <p>Before using the UltraFlex ice baths or infrared sauna, please read and agree to the following:</p>
                <p className="text-amber-400 text-xs font-medium">DISCLAIMER: By accepting, you waive certain legal rights, including the right to sue.</p>
                <ol className="list-decimal pl-5 space-y-2 text-zinc-400">
                  <li>I understand and accept all risks associated with using ice baths and infrared sauna facilities.</li>
                  <li>I have consulted a healthcare provider if I have any concerns about using these facilities.</li>
                  <li>I will avoid using these facilities if I am pregnant, on medication, or have conditions such as heart issues, pacemakers, infections, or have had recent surgery.</li>
                </ol>
              </div>
              <Checkbox testId="waiver-accept" checked={waiver} onChange={() => setWaiver(!waiver)}
                label="I have read, understood, and agree to the Ice Bath & Infrared Sauna Waiver. I certify that I accept all risks and waive my right to claim." />
            </>
          )}

          {/* === STEP 4: TERMS & CONDITIONS === */}
          {currentStep === 4 && (
            <>
              <p className="text-zinc-400 text-xs mb-2">Please read the full Terms & Conditions below. You must scroll to the bottom before accepting.</p>
              <div onScroll={handleTermsScroll}
                className="bg-zinc-950 rounded-lg p-5 text-xs text-zinc-400 leading-relaxed max-h-[350px] overflow-y-auto border border-zinc-800 space-y-4"
                data-testid="terms-scroll-area">
                <h3 className="text-white font-bold text-sm">Terms & Conditions</h3>
                <p className="text-zinc-500">Effective from October 1, 2024, until further notice.</p>

                <div><h4 className="text-zinc-200 font-semibold mb-1">1. Membership Agreement</h4>
                <p>1.1 Membership starts upon acceptance of terms in person or 7 days after acceptance if off-premises, or upon entering the gym.</p>
                <p>1.2 Your membership begins immediately, granting privileges applicable to your chosen membership type.</p>
                <p>1.3 Members must be 16 or older. Junior Membership applies to those aged 14-16.</p>
                <p>1.4 Membership is strictly non-transferable.</p>
                <p>1.5 UltraFlex reserves the right to refuse applications or membership renewals without explanation.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">2. Fees and Charges</h4>
                <p>2.1 Full payment is due with the first Direct Debit and is non-refundable.</p>
                <p>2.2 All members must meet the agreed number of Direct Debit payments monthly, regardless of attendance. For 12-month contracts, at least 12 payments are required.</p>
                <p>2.3 Members who join online are entitled to a 14-day cooling-off period during which they can cancel their membership without any obligations. However, for memberships initiated in person at the gym, the agreement becomes binding immediately upon signing.</p>
                <p>2.4 Non-payment exceeding one month may result in debt referral to a third-party agency. All associated recovery costs will be borne by the member.</p>
                <p>2.5 Any leniency in payment will not restrict UltraFlex from enforcing terms in the future.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">3. Membership Renewal</h4>
                <p>3.1 Membership fees may be adjusted with one month's notice.</p>
                <p>3.2 After the minimum Direct Debit payments, membership continues on a rolling basis with monthly payments. Refunds are not provided.</p>
                <p>3.3 For prepaid 12-month memberships with a free initial period, the membership will renew at the monthly rate unless cancelled within 30 days of the final payment.</p>
                <p>3.4 Renewal Direct Debit changes will be communicated in writing with one month's notice.</p>
                <p>3.5 Non contracted memberships require a 30-day email notice for cancellation. For 12-month contracts, all payments must be made before cancellation notice. To give notice please contact Ashbourne membership management services: memberships@ashbournemanagement.co.uk.</p>
                <p>3.6 Special offer memberships require at least six payments before requesting cancellation, with reasonable causes such as relocation or health issues.</p>
                <p>3.7 Prepaid 3, 6, or 12-month fees are strictly non-refundable.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">4. General Terms of Membership</h4>
                <p>4.1 UltraFlex is not responsible for the use of free parking at its gyms; parking is at the owner's own risk.</p>
                <p>4.2 Compliance with UltraFlex's "Rules of Membership" displayed in the gym is mandatory.</p>
                <p>4.3 UltraFlex reserves the right to amend these rules and terminate memberships if behaviour is detrimental or offensive.</p>
                <p>4.4 Members are responsible for their own health and safety.</p>
                <p>4.5 For 24/7 locations, note that staff may not be present between 9pm-6am on weekdays and 8pm-6am on weekends. Emergency call buttons are provided.</p>
                <p>4.6 Disabilities or health conditions must be disclosed before using gym facilities.</p>
                <p>4.7 Changes in health must be reported immediately.</p>
                <p>4.8 UltraFlex is not liable for undeclared health conditions.</p>
                <p>4.9 Report accidents to staff immediately.</p>
                <p>4.10 A free induction session is offered and recommended.</p>
                <p>4.11 UltraFlex is not liable for use of free parking at UltraFlex gyms; these are at the owner's risk.</p>
                <p>4.12 Parking is managed by external providers where applicable.</p>
                <p>4.13 Entry is only permitted through designated entrances.</p>
                <p>4.14 Pets, except for guide dogs, are not permitted.</p>
                <p>4.15 Personal belongings should be stored in lockers. UltraFlex is not responsible for loss or damage. No overnight use of lockers; lockers will be emptied every evening before closing time by staff. If your site has 24/7 access, the use of lockers is not permitted when the gym is unmanned.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">5. Junior Membership</h4>
                <p>5.1 Junior members (ages 14-16) must always be accompanied by an adult (18 years or older).</p>
                <p>5.2 Junior members may only use equipment under direct adult supervision and must adhere to safety guidelines.</p>
                <p>5.3 Junior members cannot set up a Direct Debit membership (minimum age 18); however, this can be done using a parent or guardian's bank account.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">6. Liability and Waiver</h4>
                <p>6.1 Members assume the risks of injury or damage associated with gym use and release UltraFlex from liability.</p>
                <p>6.2 Members confirm they are in good health and accept responsibility for any risks.</p></div>

                <div><h4 className="text-zinc-200 font-semibold mb-1">7. Assignment of Agreement</h4>
                <p>UltraFlex reserves the right to assign this agreement to a third party without affecting membership rights.</p>
                <p className="mt-2 text-zinc-500">This agreement is governed by English law.</p></div>
              </div>
              <div className="mt-4">
                {!termsScrolled && <p className="text-amber-400 text-xs mb-2">Please scroll to the bottom of the terms to continue.</p>}
                <Checkbox testId="terms-accept" checked={termsAccepted} onChange={() => { if (termsScrolled) setTermsAccepted(!termsAccepted); }}
                  label="I have read, understood, and agree to the UltraFlex Terms & Conditions (effective October 1, 2024)." />
                <div className="mt-2">
                  <Checkbox testId="marketing-optin" checked={marketingOptIn} onChange={() => setMarketingOptIn(!marketingOptIn)}
                    label="I'd like to receive news, offers and updates from UltraFlex (optional)." />
                </div>
              </div>
            </>
          )}

          {/* === STEP 5: DIGITAL SIGNATURE === */}
          {currentStep === 5 && (
            <>
              <p className="text-zinc-400 text-sm mb-4">Please type your full legal name below to digitally sign this agreement. This constitutes your electronic signature.</p>
              <div className="bg-zinc-900/50 rounded-lg p-5">
                <label className="text-zinc-400 text-xs block mb-2">Full Name (as your signature) *</label>
                <input value={signature} onChange={e => setSignature(e.target.value)} placeholder="Type your full name here"
                  className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-uf-red rounded-lg px-4 py-3 text-white text-lg font-serif italic tracking-wide"
                  autoComplete="off" data-testid="signature-input" />
                <p className="text-zinc-500 text-xs mt-2">Date: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
              {signature.trim().length >= 3 && (
                <div className="bg-zinc-900/30 rounded-lg p-4 mt-4 border border-zinc-800">
                  <p className="text-zinc-500 text-xs mb-2">Agreement Summary:</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    <span className="text-zinc-500">Declaration (5 items)</span><span className="text-emerald-400">Confirmed</span>
                    <span className="text-zinc-500">Health & Safety (6 items)</span><span className="text-emerald-400">Confirmed</span>
                    <span className="text-zinc-500">PAR-Q Health Form</span><span className="text-emerald-400">Completed</span>
                    <span className="text-zinc-500">Ice Bath & Sauna Waiver</span><span className="text-emerald-400">Accepted</span>
                    <span className="text-zinc-500">Terms & Conditions</span><span className="text-emerald-400">Accepted (v2024-10-01)</span>
                    <span className="text-zinc-500">Signed by</span><span className="text-white font-serif italic">{signature}</span>
                    <span className="text-zinc-500">Date</span><span className="text-white">{new Date().toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-uf-border bg-zinc-900/30">
          <button onClick={back} disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft className="w-4 h-4" />Back
          </button>
          <span className="text-zinc-600 text-xs">{currentStep + 1} of {STEPS.length}</span>
          <button onClick={next} disabled={!canProceed() || submitting} data-testid="agreement-next-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-uf-red text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors">
            {submitting ? 'Submitting...' : currentStep === 5 ? 'Submit Agreement' : 'Continue'}
            {currentStep < 5 && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberAgreementFlow;
